/**
 * Quotes for the Mini App. The only math imported here is @gong/curve-math:
 * buy, sell, simulateLaunch, anti-snipe. The UI never multiplies reserves itself.
 *
 * A quote is not “the price on the screen”. It is a snapshot that produces
 * min_out and a short validUntil; the transaction is assembled from those
 * numbers at the moment of signing, not from what was rendered earlier.
 */
import {
  BuyResult,
  CurveConfig,
  CurveError,
  CurveState,
  DEFAULT_CURVE_CONFIG,
  DevBuyResult,
  LaunchResult,
  SellResult,
  antiSnipeFeeBps,
  buy,
  isCurveClosed,
  remainingJettons,
  remainingTonCapacity,
  sell,
  simulateLaunch,
  spotPrice,
} from '@gong/curve-math';

import { DEFAULT_SLIPPAGE_BPS, QUOTE_TTL_SECONDS } from '../config';
import { applySlippage, unixNow } from '../lib/format';

export interface QuoteMeta {
  /** Minimum acceptable output, after default slippage. Goes into the payload. */
  minOut: bigint;
  /** Unix seconds. Same value is TON Connect validUntil and on-chain deadline. */
  deadline: number;
  quotedAt: number;
  slippageBps: bigint;
  ttlSeconds: number;
}

export type BuyQuote = BuyResult & QuoteMeta;
export type SellQuote = SellResult & QuoteMeta;

export interface LaunchQuote extends LaunchResult {
  quotedAt: number;
  publicCurveClosed: boolean;
  remainingTonAfter: bigint;
  remainingJettonsAfter: bigint;
  seedTon: bigint;
  devBuyTon: bigint;
}

export function quoteDeadline(now = unixNow(), ttlSeconds = QUOTE_TTL_SECONDS): number {
  return now + ttlSeconds;
}

export function quoteBuy(
  state: CurveState,
  tonIn: bigint,
  options: {
    now?: number;
    config?: CurveConfig;
    slippageBps?: bigint;
    ttlSeconds?: number;
  } = {},
): BuyQuote {
  const config = options.config ?? DEFAULT_CURVE_CONFIG;
  const now = options.now ?? unixNow();
  const slippageBps = options.slippageBps ?? DEFAULT_SLIPPAGE_BPS;
  const ttlSeconds = options.ttlSeconds ?? QUOTE_TTL_SECONDS;
  const result = buy(config, state, { tonIn, now });
  return {
    ...result,
    minOut: applySlippage(result.jettonsOut, slippageBps),
    deadline: quoteDeadline(now, ttlSeconds),
    quotedAt: now,
    slippageBps,
    ttlSeconds,
  };
}

export function quoteSell(
  state: CurveState,
  jettonsIn: bigint,
  options: {
    now?: number;
    config?: CurveConfig;
    slippageBps?: bigint;
    ttlSeconds?: number;
  } = {},
): SellQuote {
  const config = options.config ?? DEFAULT_CURVE_CONFIG;
  const now = options.now ?? unixNow();
  const slippageBps = options.slippageBps ?? DEFAULT_SLIPPAGE_BPS;
  const ttlSeconds = options.ttlSeconds ?? QUOTE_TTL_SECONDS;
  const result = sell(config, state, { jettonsIn, now });
  return {
    ...result,
    minOut: applySlippage(result.tonOut, slippageBps),
    deadline: quoteDeadline(now, ttlSeconds),
    quotedAt: now,
    slippageBps,
    ttlSeconds,
  };
}

export function quoteLaunch(
  seedTon: bigint,
  devBuyTon: bigint,
  options: { now?: number; config?: CurveConfig } = {},
): LaunchQuote {
  const config = options.config ?? DEFAULT_CURVE_CONFIG;
  const now = options.now ?? unixNow();
  const result = simulateLaunch(config, { seedTon, devBuyTon, now });
  return {
    ...result,
    quotedAt: now,
    publicCurveClosed: isCurveClosed(config, result.state) || result.state.phase !== 'Active',
    remainingTonAfter: remainingTonCapacity(config, result.state),
    remainingJettonsAfter: remainingJettons(config, result.state),
    seedTon,
    devBuyTon,
  };
}

export function quoteOrError<T>(fn: () => T): { ok: true; value: T } | { ok: false; error: CurveError } {
  try {
    return { ok: true, value: fn() };
  } catch (error) {
    if (error instanceof CurveError) {
      return { ok: false, error };
    }
    throw error;
  }
}

export function currentSpot(state: CurveState, config: CurveConfig = DEFAULT_CURVE_CONFIG): bigint {
  return spotPrice(config, state);
}

export function currentAntiSnipeBps(
  state: CurveState,
  now = unixNow(),
  config: CurveConfig = DEFAULT_CURVE_CONFIG,
): bigint {
  return antiSnipeFeeBps(config, state, now);
}

export type { DevBuyResult };
